import React from 'react';
import SidebarCard from './SidebarCard';
import Field, { CheckField } from './Field';
import { showsMap } from './mapFlag';

const PAGE_TYPES = [
  { value: '', label: 'Default (article)' },
  { value: 'article', label: 'Article' },
  { value: 'category', label: 'Category' },
  { value: 'about', label: 'About' },
  { value: 'full_width', label: 'Full width' },
];

const PAGE_TYPE_HELP = {
  '': 'Standard layout with a sidebar.',
  article: 'Standard layout with a sidebar.',
  category: 'Lists this page’s child pages.',
  about: 'Full-width layout for about pages.',
  full_width: 'Full-width layout that reads like an article.',
};

/**
 * Slug, primary topic, page type, parent, flags and the Firestore link.
 * `errors` carries inline messages keyed by field ({ slug, primary_topic }).
 */
export default function DetailsCard({
  page,
  isNewPage,
  pageId,
  availablePages,
  errors = {},
  open,
  onToggle,
  onChange,
  onEditParent,
  firestoreUrl,
}) {
  return (
    <SidebarCard id="details" title="Details" open={open} onToggle={onToggle}>
      <Field
        id="page-slug"
        label="Slug"
        required
        error={errors.slug}
        hint={isNewPage ? 'URL path. Follows the title until you edit it.' : 'Locked once the page exists.'}
      >
        <input
          id="page-slug"
          type="text"
          className={`input mono${errors.slug ? ' is-invalid' : ''}`}
          value={page.slug}
          onChange={(e) => onChange('slug', e.target.value)}
          placeholder="my-page-path"
          disabled={!isNewPage}
          aria-invalid={!!errors.slug}
          aria-describedby={errors.slug ? 'page-slug-error' : 'page-slug-hint'}
        />
      </Field>

      <Field
        id="page-primary-topic"
        label="Primary topic"
        error={errors.primary_topic}
        hint="Required before a main image can be chosen; names the image file."
      >
        <input
          id="page-primary-topic"
          type="text"
          className={`input${errors.primary_topic ? ' is-invalid' : ''}`}
          value={page.primary_topic || ''}
          onChange={(e) => onChange('primary_topic', e.target.value)}
          placeholder="e.g. Recipes"
          aria-invalid={!!errors.primary_topic}
          aria-describedby={errors.primary_topic ? 'page-primary-topic-error' : 'page-primary-topic-hint'}
        />
      </Field>

      <Field id="page-type" label="Page type" hint={PAGE_TYPE_HELP[page.page_type || ''] || 'Changes the page layout.'}>
        <select id="page-type" className="select" value={page.page_type || ''} onChange={(e) => onChange('page_type', e.target.value)}>
          {PAGE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </Field>

      <Field
        id="page-parent"
        label="Parent page"
        hint="Builds a hierarchy within this domain."
        labelExtra={page.parent_page ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onEditParent} style={{ padding: '0 4px' }}>
            Open parent ↗
          </button>
        ) : null}
      >
        <select id="page-parent" className="select" value={page.parent_page || ''} onChange={(e) => onChange('parent_page', e.target.value)}>
          <option value="">No parent page</option>
          {(availablePages || [])
            .filter((p) => p.id !== pageId)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.title || p.slug || p.id}{p.slug && p.title ? ` (${p.slug})` : ''}
              </option>
            ))}
        </select>
      </Field>

      <CheckField
        id="page-hide-main-image"
        label="Hide the main image"
        checked={page.hide_main_image}
        onChange={(v) => onChange('hide_main_image', v)}
      />
      <CheckField
        id="page-show-map"
        label="Show map"
        checked={showsMap(page)}
        onChange={(v) => onChange('show_map', v)}
        hint="A map of the list items' addresses above the list. Needs two or more list items whose description opens with an address."
      />
      <CheckField
        id="page-has-children"
        label="This page has child pages"
        checked={page.has_children}
        onChange={(v) => onChange('has_children', v)}
      />

      {firestoreUrl && (
        <div style={{ marginTop: 4, borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
          <a
            href={firestoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-sm"
            style={{ padding: '2px 4px', fontSize: 12, color: 'var(--color-muted)' }}
          >
            Open in Firestore ↗
          </a>
        </div>
      )}
    </SidebarCard>
  );
}
